import { EventTypes } from '@candulabs/craft';
import {
  Button,
  Caption,
  ColorKeys,
  css,
  EditIcon,
  Inline,
  LightningIcon,
  ObjectIcon,
  PlacementIcon,
  Stack,
  styled,
  Tooltip,
} from 'canoe';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useEventing } from 'commons/eventing';
import { Flags, useFlags } from 'commons/hooks';
import { Layer } from 'craft/components/Layer';
import { Shortcut } from 'craft/components/Shortcuts';
import { useActiveTooltip } from 'craft/components/Tooltip';
import { CRAFT_ROOT_ID } from 'craft/constants';
import { useCraftPropsContext } from 'craft/contexts';
import { useEditorSelector, useSelection, useTooltipPlacement } from 'craft/hooks';
import {
  setToolboxTab,
  useCurrentSnapshot,
  useIsPalettePinned,
  usePaletteTab,
  useToolboxState,
  useToolboxTab,
} from 'craft/state';
import { ToolboxTabs } from 'craft/types';
import { ViewportDropdown } from 'dashboard/components/ViewportDropdown';

import { EmptyPlaceholder, EmptyStateText } from './EmptyPlaceholder';
import { Nav, NavItem } from './Nav';
import Container from './Responsive';
import { TOOLBOX_CONTENT, TOOLBOX_ID } from './constants';
import { Component } from './tabs/Component';
import { Interactions } from './tabs/Interactions';
import { Placements } from './tabs/Placements';

const Tab = styled.div`
  height: 100%;
  width: 300px;
  overflow: auto;
  // Allow toolbox to scroll a bit further to allow for the (potential) intercom button
  padding-bottom: 70px;
`;

const EmptyStateIcon = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.OnyxGrayD10};
    padding: ${theme.spaces.xs};
    border-radius: ${theme.borderRadius.xsmall};
  `}
`;

const CloseToolbox = styled.div`
  position: absolute;
  top: 6px;
  right: 10px;
`;

export const Toolbox = React.memo(() => {
  const {
    [Flags.INLINE_SNAPSHOTS]: inlineSnapshots,
    [Flags.DOCKED_TOOLBOX]: dockedToolbox,
  } = useFlags();
  const { isToolboxDocked, setDockedState, forceOpenToolbox } = useToolboxState();

  const isEditorDragging = useEditorSelector((state) => state.events[EventTypes.dragged].size > 0);
  const isPalettePinned = useIsPalettePinned();
  const paletteTab = usePaletteTab();
  const { selection } = useSelection();
  const dispatch = useDispatch();
  const { contentMetadata } = useCraftPropsContext();
  const { activeTab, forcePlacementsTab } = useToolboxTab();
  const { snapshot, isSnapshotLoaded } = useCurrentSnapshot();
  const activeTooltipNode = useActiveTooltip();
  const { placement: tooltipPlacement } = useTooltipPlacement(activeTooltipNode?.id);
  const { track } = useEventing();

  useEffect(() => {
    // TODO: find a better way to handle this centrally with other content types
    // if no selector has been set for the hotspot, load the toolbox with Placements tab
    if (activeTooltipNode && !tooltipPlacement?.selector) {
      dispatch(setToolboxTab(ToolboxTabs.Placements));
      // when automatically switching to placements tab, we want to show the toolbox
      forceOpenToolbox();
    }
  }, []);

  const Sections = {
    [ToolboxTabs.Component]: Component,
    [ToolboxTabs.Interactions]: Interactions,
    ...((snapshot || inlineSnapshots) && contentMetadata?.personalisationMode !== 'Experimentation'
      ? {
          [ToolboxTabs.Placements]: Placements,
        }
      : {}),
  };

  const Content = useMemo(() => Sections[activeTab], [activeTab]);

  const mode = activeTab === ToolboxTabs.Placements ? 'Dark' : 'Light';

  const getIconColor = useCallback(
    (tab) => {
      if (forcePlacementsTab && tab !== ToolboxTabs.Placements) {
        // disabled icon color
        if (mode === 'Dark') {
          return 'OnyxGrayL30' as ColorKeys;
        }
        return 'GlitterGrayD20' as ColorKeys;
      }

      if (mode === 'Dark') {
        return (activeTab === tab ? 'AquaTealL10' : 'GlitterGrayL30') as ColorKeys;
      }
      return (activeTab === tab ? 'BrandPurple' : 'OnyxGray') as ColorKeys;
    },
    [activeTab, forcePlacementsTab, mode],
  );

  if (isToolboxDocked || (snapshot && !isSnapshotLoaded)) {
    return null;
  }

  if (!isPalettePinned && isEditorDragging && paletteTab !== 'Layers') {
    return (
      <Container id={TOOLBOX_ID} data-walkthrough="editor-toolbox" mode={mode}>
        <div css={{ width: '300px' }}>
          <Layer />
        </div>
      </Container>
    );
  }

  return (
    <Container id={TOOLBOX_ID} data-walkthrough="editor-toolbox" mode={mode}>
      {dockedToolbox && !forcePlacementsTab && (
        <CloseToolbox>
          <Button
            appearance="text"
            showUnderlined
            variant={mode === 'Dark' ? 'white' : 'contrast'}
            onClick={() => {
              setDockedState(true);
              track('editor.dockedToolbox.close');
            }}
            size="xsmall"
          >
            Close
          </Button>
        </CloseToolbox>
      )}

      <ViewportDropdown />

      <Nav numOfTabs={Object.keys(Sections).length} mode={mode}>
        {Object.keys(Sections).map((tab) => (
          <Tooltip
            key={tab}
            additionalStyle={{ borderTop: 'none' }}
            placement="bottom"
            content={
              <Caption color="White" m="none">
                {tab}&nbsp;
                <Shortcut enabled>{tab[0].toUpperCase()}</Shortcut>
              </Caption>
            }
          >
            <NavItem
              id={`editor-toolbox-tab-${tab.toLowerCase()}`}
              key={tab}
              active={tab === activeTab}
              disabled={forcePlacementsTab && tab !== ToolboxTabs.Placements}
              onClick={() => !forcePlacementsTab && dispatch(setToolboxTab(tab))}
              mode={mode}
            >
              {tab === ToolboxTabs.Component && <EditIcon color={getIconColor(tab)} />}
              {tab === ToolboxTabs.Interactions && <LightningIcon color={getIconColor(tab)} />}
              {tab === ToolboxTabs.Placements && <PlacementIcon color={getIconColor(tab)} />}
            </NavItem>
          </Tooltip>
        ))}
      </Nav>

      {!!Content && (
        <Tab id={TOOLBOX_CONTENT}>
          {selection.isEmpty && activeTab !== ToolboxTabs.Placements && (
            <EmptyPlaceholder
              title="No component selected."
              subtitle={
                <Inline space="xs" wrap="nowrap">
                  <EmptyStateIcon>
                    <ObjectIcon color="GlitterGrayD10" />
                  </EmptyStateIcon>
                  <Stack space="s">
                    <EmptyStateText size="short">
                      Use the toolbar on the left to drag components in.
                    </EmptyStateText>
                    <EmptyStateText size="short">
                      Or select a component you already have.
                    </EmptyStateText>
                  </Stack>
                </Inline>
              }
            />
          )}

          {(!selection.isEmpty || activeTab === ToolboxTabs.Placements) && (
            <>
              <Content
                key={activeTab}
                nodeIds={activeTab === ToolboxTabs.Placements ? [CRAFT_ROOT_ID] : selection.all}
              />
            </>
          )}
        </Tab>
      )}
    </Container>
  );
});
